import React from 'react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuth from '../../auth/feature/auth';
import { adminService, IAdminUser, ILoginEvent } from '../service/admin.service';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { UserLicensesModal } from './UserLicensesModal';
import { RISER_PRODUCT_SLUG } from '../../riser/feature/use-riser-page';
import { SBFRAMEWORK_PRODUCT_SLUG } from '../../sbframework/feature/use-sbframework-access';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

// Browser and OS names are product identifiers and stay verbatim in every
// language; only the "unknown" fallback is a label and gets translated.
const parseUserAgent = (ua: string | null, t: TFunction): string => {
  const unknown = t('admin.dashboard.loginHistory.unknown');
  if (!ua) return unknown;
  const browsers: [RegExp, string][] = [
    [/Edg\//, 'Edge'],
    [/OPR\//, 'Opera'],
    [/Chrome\//, 'Chrome'],
    [/Firefox\//, 'Firefox'],
    [/Safari\//, 'Safari']
  ];
  const oses: [RegExp, string][] = [
    [/Windows NT/, 'Windows'],
    [/Mac OS X/, 'macOS'],
    [/Linux/, 'Linux'],
    [/Android/, 'Android'],
    [/iPhone|iPad/, 'iOS']
  ];
  const browser = browsers.find(([re]) => re.test(ua))?.[1] ?? unknown;
  const os = oses.find(([re]) => re.test(ua))?.[1] ?? unknown;
  return `${browser} · ${os}`;
};

// Compact product labels so pills stay scannable in the table.
const PRODUCT_SHORT_NAMES: Record<string, string> = {
  'Squarebit Subdivs': 'Subdivs'
};

const shortProductName = (name: string) => PRODUCT_SHORT_NAMES[name] ?? name;

const LicenseTypeBadge = ({
  type,
  licenseKey,
  productName
}: {
  type: string;
  licenseKey: string;
  productName?: string | null;
}) => {
  const { t } = useTranslation();
  // `label` is the license-type identifier as stored — never translated.
  const normalized = type.toLowerCase();
  const label = normalized === 'time-limited' ? 'paid' : normalized;
  const colors: Record<string, string> = {
    trial: 'bg-badge-red-bg border-badge-red-text text-badge-red-text',
    monthly: 'bg-badge-green-bg border-badge-green-text text-badge-green-text',
    annual: 'bg-badge-green-bg border-badge-green-text text-badge-green-text',
    yearly: 'bg-badge-green-bg border-badge-green-text text-badge-green-text',
    // Perpetual (one-time) gets its own colour so it reads distinctly from recurring subs.
    perpetual: 'bg-badge-sky-bg border-badge-sky-text text-badge-sky-text',
    paid: 'bg-badge-green-bg border-badge-green-text text-badge-green-text'
  };
  const style =
    colors[label] ?? 'bg-badge-sky-bg border-badge-sky-text text-badge-sky-text';
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(licenseKey);
        toast.success(t('admin.dashboard.toast.keyCopied'));
      }}
      title={t('admin.dashboard.licenseBadgeTitle', {
        product: productName ?? t('admin.dashboard.unknownProduct'),
        licenseKey
      })}
      className={`inline-block px-[9px] py-[3px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] uppercase w-fit cursor-pointer hover:brightness-95 transition-all ${style}`}
    >
      {productName ? `${shortProductName(productName)} · ${label}` : label}
    </button>
  );
};

type ModalState =
  | { type: 'reset-trial'; user: IAdminUser }
  | { type: 'account-actions'; user: IAdminUser }
  | { type: 'delete'; user: IAdminUser }
  | { type: 'notes'; user: IAdminUser }
  | { type: 'login-history'; user: IAdminUser }
  | { type: 'grant-eye'; user: IAdminUser }
  | { type: 'revoke-eye'; user: IAdminUser }
  | { type: 'grant-riser'; user: IAdminUser }
  | { type: 'revoke-riser'; user: IAdminUser }
  | { type: 'seats'; user: IAdminUser }
  | { type: 'grant-sbframework'; user: IAdminUser }
  | { type: 'revoke-sbframework'; user: IAdminUser }
  | { type: 'licenses'; user: IAdminUser }
  | { type: 'download-stats' }
  | null;

/** Whether a user holds a given product grant, from the slug list. */
const hasGrant = (user: IAdminUser, slug: string) =>
  (user.grants ?? []).includes(slug);

/** DB Product.name for the framework, as it appears on a licence row. */
const FRAMEWORK_PRODUCT_NAME = 'Squarebit Framework';

/**
 * The framework licence on this account, if it has one.
 *
 * Seats are edited per licence, and the framework is the only product whose
 * seat count belongs in this panel: TouchPose and Subdivs seats come from the
 * Stripe checkout quantity, so setting them here would leave the licence
 * disagreeing with what the customer is billed for. The server refuses those
 * too; this just keeps them out of the UI.
 */
const frameworkKey = (user: IAdminUser) =>
  user.License_key.find((k) => k.product?.name === FRAMEWORK_PRODUCT_NAME);

/**
 * Granted the framework but holding no key for it.
 *
 * The grant path issues the key best-effort: a LicenseSpring failure leaves the
 * grant standing rather than losing the entitlement over it. So this state is
 * reachable, and without surfacing it an admin has no way to know the account
 * can download the framework but cannot run it.
 */
const needsFrameworkKey = (user: IAdminUser) =>
  hasGrant(user, SBFRAMEWORK_PRODUCT_SLUG) && !frameworkKey(user);

/**
 * Compact granted/not-granted pill for one product entitlement; clicking opens
 * the grant/revoke confirmation modal.
 *
 * Product-agnostic, because there are two of these now. The row is told
 * whether the grant is held and what to say about it, which is all that
 * differs between them.
 */
const AccessPill = ({
  granted,
  grantedLabel,
  grantedTitle,
  noneTitle,
  onClick
}: {
  granted: boolean;
  grantedLabel: string;
  grantedTitle: string;
  noneTitle: string;
  onClick: () => void;
}) => {
  return granted ? (
    <button
      onClick={onClick}
      title={grantedTitle}
      className="inline-flex items-center gap-[5px] px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-badge-green-bg border border-badge-green-text text-badge-green-text hover:brightness-95 transition-colors"
    >
      <span className="w-[6px] h-[6px] rounded-full bg-green-500 shrink-0" />
      {grantedLabel}
    </button>
  ) : (
    <button
      onClick={onClick}
      title={noneTitle}
      className="inline-flex items-center justify-center px-[10px] py-[3px] rounded-[6px] text-[11px] font-medium bg-surface-secondary-alt border border-border-dark/[0.15] text-content-muted hover:brightness-95 transition-colors"
    >
      —
    </button>
  );
};

export const DashboardSection = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<ModalState>(null);
  const [notesText, setNotesText] = React.useState('');
  // Pending seat edits, keyed by licence id. A ref rather than state because
  // the inputs are uncontrolled (defaultValue) - re-rendering the modal on
  // every keystroke would fight the number spinner for no benefit.
  const seatsDraft = React.useRef<Record<number, number>>({});
  // Two-step confirm for removing a key, kept local to the modal rather than
  // routed through a second dialog on top of the first.
  const [confirmRemoveKey, setConfirmRemoveKey] = React.useState(false);
  const [sortKey, setSortKey] = React.useState<string>('createdAt');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [filter, setFilter] = React.useState<string>('all');
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 25;

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminService.getUsers(),
    enabled: isAdmin
  });

  const resetTrialMutation = useMutation({
    mutationFn: (userId: number) => adminService.resetTrial(userId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.trialReset'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.trialResetError'))
  });

  const resendActivationMutation = useMutation({
    mutationFn: (userId: number) => adminService.resendActivation(userId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.activationSent'));
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.activationSendError'))
  });

  const forceActivateMutation = useMutation({
    mutationFn: (userId: number) => adminService.forceActivate(userId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.accountActivated'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.accountActivateError'))
  });

  // Riser is invite-only, so this pill is the whole distribution mechanism for
  // it: an account sees the product page, the menu entry and the app itself
  // only once a grant row exists. The endpoint has always taken a slug, so
  // there was nothing to add on the API side.
  const riserAccessMutation = useMutation({
    mutationFn: ({ userId, grant }: { userId: number; grant: boolean }) =>
      adminService.setProductAccess(userId, RISER_PRODUCT_SLUG, grant),
    onSuccess: (_data, { grant }) => {
      toast.success(
        grant
          ? t('admin.dashboard.toast.riserGranted')
          : t('admin.dashboard.toast.riserRevoked')
      );
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.riserError'))
  });

  // The framework grant does two things the other two do not: it unlocks the
  // download, and it makes the account hold a LicenseSpring 'mix' licence -
  // adopting the one that email already has where there is one, rather than
  // issuing a second. That all happens server-side off the same endpoint; from
  // here it is the same pill.
  const frameworkAccessMutation = useMutation({
    mutationFn: ({ userId, grant }: { userId: number; grant: boolean }) =>
      adminService.setProductAccess(userId, SBFRAMEWORK_PRODUCT_SLUG, grant),
    onSuccess: (_data, { grant }) => {
      toast.success(
        grant
          ? t('admin.dashboard.toast.sbframeworkGranted')
          : t('admin.dashboard.toast.sbframeworkRevoked')
      );
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.sbframeworkError'))
  });

  // Retire the framework licence upstream and drop it here, so the next issue
  // mints a new key. Confirmed in the modal before it fires: the old key stops
  // working the moment this lands, on every machine using it.
  const removeKeyMutation = useMutation({
    mutationFn: (userId: number) => adminService.removeFrameworkLicense(userId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.keyRemoved'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setConfirmRemoveKey(false);
      setModal(null);
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('admin.dashboard.toast.keyRemoveError');
      toast.error(message);
    }
  });

  // Retry issuing a framework key. Idempotent server-side, so a double click
  // returns the existing key rather than minting a second one.
  const issueKeyMutation = useMutation({
    mutationFn: (userId: number) => adminService.issueFrameworkLicense(userId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.keyIssued'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('admin.dashboard.toast.keyIssueError');
      toast.error(message);
    }
  });

  // Activation limit for a LicenseSpring key. TouchPose buyers choose this at
  // checkout; the invite-only products have no checkout, so this is the only
  // place it can be set. The server refuses to drop it below the number of
  // machines currently activated, and that message is what the toast shows.
  const seatsMutation = useMutation({
    mutationFn: ({ licenseId, seats }: { licenseId: number; seats: number }) =>
      adminService.setLicenseSeats(licenseId, seats),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.seatsUpdated'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('admin.dashboard.toast.seatsError');
      toast.error(message);
    }
  });

  const eyeAccessMutation = useMutation({
    mutationFn: ({ userId, grant }: { userId: number; grant: boolean }) =>
      adminService.setEyeAccess(userId, grant),
    onSuccess: (_data, { grant }) => {
      toast.success(
        grant
          ? t('admin.dashboard.toast.eyeGranted')
          : t('admin.dashboard.toast.eyeRevoked')
      );
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.eyeError'))
  });

  const loginHistoryUserId = modal?.type === 'login-history' ? modal.user.id : null;
  const loginHistoryQuery = useQuery({
    queryKey: ['login-history', loginHistoryUserId],
    queryFn: () => adminService.getLoginHistory(loginHistoryUserId!),
    enabled: loginHistoryUserId !== null
  });

  const downloadStatsQuery = useQuery({
    queryKey: ['download-stats'],
    queryFn: () => adminService.getDownloadStats(),
    enabled: modal?.type === 'download-stats'
  });

  const updateNotesMutation = useMutation({
    mutationFn: ({ userId, notes }: { userId: number; notes: string }) =>
      adminService.updateNotes(userId, notes),
    onSuccess: () => {
      toast.success(t('admin.dashboard.toast.noteSaved'));
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.noteSaveError'))
  });

  const [emailOnDelete, setEmailOnDelete] = React.useState(true);

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => adminService.deleteUser(userId, emailOnDelete),
    onSuccess: (result) => {
      if (result.warnings.length > 0) {
        toast.success(t('admin.dashboard.toast.accountDeletedWithWarnings'));
        // Warnings come from the API verbatim — shown as returned.
        for (const w of result.warnings) {
          toast.error(w, { duration: 8000 });
        }
      } else {
        toast.success(t('admin.dashboard.toast.accountDeleted'));
      }
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setModal(null);
    },
    onError: () => toast.error(t('admin.dashboard.toast.accountDeleteError'))
  });

  React.useEffect(() => {
    if (!isAdmin) navigate(ROUTER_LINKS.ROOT);
  }, [isAdmin, navigate]);

  React.useEffect(() => {
    if (usersQuery.error) toast.error(t('admin.dashboard.toast.usersLoadError'));
  }, [usersQuery.error, t]);

  if (!isAdmin) return null;

  const allUsers = usersQuery.data?.users ?? [];
  const mrr = usersQuery.data?.mrr ?? 0;
  const totalDownloads = usersQuery.data?.totalDownloads ?? 0;
  const now = new Date();

  const filteredUsers = React.useMemo(() => {
    let users = allUsers;
    if (search.trim())
      users = users.filter((u) =>
        u.email.toLowerCase().includes(search.toLowerCase())
      );
    if (filter === 'unactivated') users = users.filter((u) => !u.isActivated);
    else if (filter === 'paid')
      users = users.filter((u) =>
        u.License_key.some(
          (k) =>
            (k.type === 'monthly' ||
              k.type === 'annual' ||
              k.type === 'yearly' ||
              k.type === 'perpetual') &&
            new Date(k.expiresAt) >= now
        )
      );
    else if (filter === 'trial')
      users = users.filter((u) =>
        u.License_key.some((k) => k.type === 'trial' && new Date(k.expiresAt) >= now)
      );
    else if (filter === 'expired')
      users = users.filter(
        (u) =>
          u.License_key.length > 0 &&
          u.License_key.every((k) => new Date(k.expiresAt) < now)
      );
    else if (filter === 'locked') users = users.filter((u) => u.isLocked);

    users = [...users].sort((a, b) => {
      let av: string | number | Date | undefined,
        bv: string | number | Date | undefined;
      if (sortKey === 'email') {
        av = a.email.toLowerCase();
        bv = b.email.toLowerCase();
      } else if (sortKey === 'createdAt') {
        av = a.createdAt;
        bv = b.createdAt;
      } else if (sortKey === 'status') {
        av = a.isActivated ? 1 : 0;
        bv = b.isActivated ? 1 : 0;
      } else if (sortKey === 'license') {
        av = a.License_key.length;
        bv = b.License_key.length;
      } else if (sortKey === 'expires') {
        av = a.License_key[0]?.expiresAt ?? '';
        bv = b.License_key[0]?.expiresAt ?? '';
      } else if (sortKey === 'lastLogin') {
        av = a.lastLoginAt ?? '';
        bv = b.lastLoginAt ?? '';
      } else if (sortKey === 'trial') {
        av = a.trialAvailable ? 1 : 0;
        bv = b.trialAvailable ? 1 : 0;
      } else if (sortKey === 'eye') {
        av = a.eyeAccess ? 1 : 0;
        bv = b.eyeAccess ? 1 : 0;
      } else if (sortKey === 'riser') {
        av = hasGrant(a, RISER_PRODUCT_SLUG) ? 1 : 0;
        bv = hasGrant(b, RISER_PRODUCT_SLUG) ? 1 : 0;
      } else if (sortKey === 'sbframework') {
        av = hasGrant(a, SBFRAMEWORK_PRODUCT_SLUG) ? 1 : 0;
        bv = hasGrant(b, SBFRAMEWORK_PRODUCT_SLUG) ? 1 : 0;
      } else {
        av = a.createdAt;
        bv = b.createdAt;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return users;
  }, [allUsers, search, filter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [search, filter, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortHeader = ({
    label,
    sortId,
    className
  }: {
    label: string;
    sortId: string;
    className: string;
  }) => (
    <th
      onClick={() => handleSort(sortId)}
      className={`${className} cursor-pointer select-none hover:text-content-dark transition-colors`}
    >
      {label} {sortKey === sortId ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const isPending =
    resetTrialMutation.isPending ||
    resendActivationMutation.isPending ||
    forceActivateMutation.isPending ||
    deleteUserMutation.isPending ||
    updateNotesMutation.isPending ||
    eyeAccessMutation.isPending ||
    riserAccessMutation.isPending ||
    frameworkAccessMutation.isPending ||
    seatsMutation.isPending ||
    issueKeyMutation.isPending ||
    removeKeyMutation.isPending;

  return (
    <div className="w-full max-w-[1400px] mx-auto py-[40px] px-[20px]">
      {/* Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[3px]"
          onClick={() => setModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`bg-surface-base rounded-[20px] shadow-xl px-[32px] py-[28px] w-full mx-[20px] ${
              modal.type === 'login-history' ||
              modal.type === 'download-stats' ||
              modal.type === 'licenses'
                ? 'max-w-[560px]'
                : 'max-w-[420px]'
            }`}
          >
            {modal.type === 'reset-trial' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.resetTrial.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.resetTrial.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => resetTrialMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-amber-500 text-surface-base hover:bg-amber-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.resetTrial.pending')
                      : t('admin.dashboard.resetTrial.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'grant-riser' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.grantRiser.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.grantRiser.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      riserAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: true
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.grantRiser.pending')
                      : t('admin.dashboard.grantRiser.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'revoke-riser' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.revokeRiser.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.revokeRiser.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      riserAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: false
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-red-500 text-surface-base hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.revokeRiser.pending')
                      : t('admin.dashboard.revokeRiser.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'seats' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.seats.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[18px]">
                  <Trans
                    i18nKey="admin.dashboard.seats.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex flex-col gap-[10px] mb-[24px]">
                  {!frameworkKey(modal.user) && (
                    <p className="text-[13px] text-gray-500">
                      {t('admin.dashboard.seats.none')}
                    </p>
                  )}
                  {modal.user.License_key.filter(
                    (license) => license.product?.name === FRAMEWORK_PRODUCT_NAME
                  ).map((license) => (
                    <div
                      key={license.id}
                      className="flex items-center justify-between gap-[12px] px-[12px] py-[10px] rounded-[10px] border border-border-dark/[0.12]"
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-content-dark truncate">
                          {license.product?.name ?? 'Unknown'}
                        </div>
                        <div className="font-mono text-[11px] text-gray-500 truncate">
                          {license.key}
                        </div>
                      </div>
                      <div className="flex items-center gap-[6px] shrink-0">
                        <label
                          className="text-[11px] uppercase tracking-wider text-gray-500"
                          htmlFor={`seats-${license.id}`}
                        >
                          {t('admin.dashboard.seats.label')}
                        </label>
                        <input
                          id={`seats-${license.id}`}
                          type="number"
                          min={1}
                          max={500}
                          defaultValue={license.maxActivations}
                          onChange={(e) => {
                            seatsDraft.current[license.id] = Number(e.target.value);
                          }}
                          className="w-[70px] px-[8px] py-[5px] rounded-[8px] border border-border-dark/[0.2] bg-surface-base text-[13px] text-content-dark"
                        />
                        <button
                          type="button"
                          disabled={isPending}
                          onClick={() => {
                            const next =
                              seatsDraft.current[license.id] ??
                              license.maxActivations;
                            if (next === license.maxActivations) {
                              setModal(null);
                              return;
                            }
                            seatsMutation.mutate({
                              licenseId: license.id,
                              seats: next
                            });
                          }}
                          className="px-[12px] py-[6px] rounded-[8px] text-[12px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 disabled:opacity-50 transition-colors"
                        >
                          {t('admin.dashboard.seats.save')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Replacing a key is a separate act from resizing one, and a
                    destructive one: every machine on the old key stops at its
                    next check. Two steps, and the danger is spelled out. */}
                {frameworkKey(modal.user) && (
                  <div className="mb-[18px] pt-[16px] border-t border-gray-200">
                    {confirmRemoveKey ? (
                      <div className="flex flex-col gap-[10px]">
                        <p className="text-[13px] text-red-700">
                          {t('admin.dashboard.seats.removeConfirm')}
                        </p>
                        <div className="flex gap-[8px]">
                          <button
                            disabled={isPending}
                            onClick={() => removeKeyMutation.mutate(modal.user.id)}
                            className="px-[14px] py-[7px] rounded-[9px] text-[13px] font-medium bg-red-500 text-surface-base hover:bg-red-600 disabled:opacity-50 transition-colors"
                          >
                            {isPending
                              ? t('admin.dashboard.seats.removePending')
                              : t('admin.dashboard.seats.removeConfirmCta')}
                          </button>
                          <button
                            onClick={() => setConfirmRemoveKey(false)}
                            className="px-[14px] py-[7px] rounded-[9px] text-[13px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemoveKey(true)}
                        className="text-[13px] font-medium text-red-600 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                      >
                        {t('admin.dashboard.seats.remove')}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => {
                      setConfirmRemoveKey(false);
                      setModal(null);
                    }}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'grant-sbframework' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.grantFramework.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.grantFramework.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      frameworkAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: true
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.grantFramework.pending')
                      : t('admin.dashboard.grantFramework.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'revoke-sbframework' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.revokeFramework.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.revokeFramework.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      frameworkAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: false
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-red-500 text-surface-base hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.revokeFramework.pending')
                      : t('admin.dashboard.revokeFramework.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'grant-eye' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.grantEye.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.grantEye.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      eyeAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: true
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.grantEye.pending')
                      : t('admin.dashboard.grantEye.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'revoke-eye' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.revokeEye.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[24px]">
                  <Trans
                    i18nKey="admin.dashboard.revokeEye.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      eyeAccessMutation.mutate({
                        userId: modal.user.id,
                        grant: false
                      })
                    }
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-red-500 text-surface-base hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.revokeEye.pending')
                      : t('admin.dashboard.revokeEye.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'account-actions' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[4px]">
                  {t('admin.dashboard.accountActions.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[20px]">
                  <Trans
                    i18nKey="admin.dashboard.accountActions.subtitle"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <div className="flex flex-col gap-[10px] mb-[20px]">
                  <button
                    onClick={() => resendActivationMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="w-full flex items-center gap-[10px] px-[14px] py-[10px] rounded-[10px] border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left disabled:opacity-50"
                  >
                    <span className="text-[16px]">📧</span>
                    <div>
                      <p className="text-[13px] font-medium text-blue-800">
                        {t('admin.dashboard.accountActions.resendTitle')}
                      </p>
                      <p className="text-[11px] text-blue-600">
                        {t('admin.dashboard.accountActions.resendBody')}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={() => forceActivateMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="w-full flex items-center gap-[10px] px-[14px] py-[10px] rounded-[10px] border border-green-200 bg-green-50 hover:bg-green-100 transition-colors text-left disabled:opacity-50"
                  >
                    <span className="text-[16px]">✅</span>
                    <div>
                      <p className="text-[13px] font-medium text-green-800">
                        {t('admin.dashboard.accountActions.forceTitle')}
                      </p>
                      <p className="text-[11px] text-green-600">
                        {t('admin.dashboard.accountActions.forceBody')}
                      </p>
                    </div>
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'delete' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[8px]">
                  {t('admin.dashboard.deleteAccount.title')}
                </h2>
                <p className="text-[14px] text-gray-900 dark:text-gray-700 mb-[6px]">
                  <Trans
                    i18nKey="admin.dashboard.deleteAccount.body"
                    values={{ email: modal.user.email }}
                    components={{
                      usec: <span className="font-medium text-content-dark" />
                    }}
                  />
                </p>
                <p className="text-[13px] text-red-500 font-medium mb-[16px]">
                  {t('admin.dashboard.deleteAccount.warning')}
                </p>
                <label className="flex items-center gap-[8px] mb-[24px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailOnDelete}
                    onChange={(e) => setEmailOnDelete(e.target.checked)}
                    className="w-[16px] h-[16px] rounded accent-red-500"
                  />
                  <span className="text-[13px] text-gray-900 dark:text-gray-700">
                    {t('admin.dashboard.deleteAccount.emailUser')}
                  </span>
                </label>
                <div className="flex gap-[10px] justify-end">
                  <button
                    onClick={() => setModal(null)}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => deleteUserMutation.mutate(modal.user.id)}
                    disabled={isPending}
                    className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-red-500 text-surface-base hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isPending
                      ? t('admin.dashboard.deleteAccount.pending')
                      : t('admin.dashboard.deleteAccount.confirm')}
                  </button>
                </div>
              </>
            )}

            {modal.type === 'licenses' && (
              <UserLicensesModal user={modal.user} onClose={() => setModal(null)} />
            )}

            {modal.type === 'login-history' && (
              <>
                <div className="flex items-start justify-between mb-[16px]">
                  <div>
                    <h2 className="text-[18px] font-display font-bold text-content-dark">
                      {t('admin.dashboard.loginHistory.title')}
                    </h2>
                    <p className="text-[13px] text-gray-900 dark:text-gray-700 mt-[2px] truncate max-w-xs">
                      {modal.user.email}
                    </p>
                  </div>
                  <button
                    onClick={() => setModal(null)}
                    className="text-gray-600 hover:text-gray-900 text-[22px] leading-none mt-[2px] font-medium"
                  >
                    ×
                  </button>
                </div>
                {loginHistoryQuery.isLoading ? (
                  <div className="flex justify-center py-[32px]">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : (loginHistoryQuery.data ?? []).length === 0 ? (
                  <p className="text-[14px] text-gray-900 dark:text-gray-700 text-center py-[24px]">
                    {t('admin.dashboard.loginHistory.empty')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-[6px] max-h-[400px] overflow-y-auto pr-[4px]">
                    {(loginHistoryQuery.data as ILoginEvent[]).map((event) => (
                      <div
                        key={event.id}
                        className={`flex items-start gap-[10px] px-[12px] py-[10px] rounded-[10px] border ${event.success ? 'bg-surface-base border-border-dark/[0.08]' : 'bg-red-50 border-red-200'}`}
                      >
                        <span
                          className={`mt-[3px] w-[7px] h-[7px] rounded-full shrink-0 ${event.success ? 'bg-green-500' : 'bg-red-500'}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-[8px]">
                            <span className="font-mono text-[11px] text-gray-900 dark:text-gray-700">
                              {event.ipAddress ?? '—'}
                            </span>
                            <span className="text-[11px] text-gray-500 shrink-0">
                              {formatDateTime(event.createdAt)}
                            </span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-[2px]">
                            {parseUserAgent(event.userAgent, t)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {modal.type === 'notes' && (
              <>
                <h2 className="text-[18px] font-display font-bold text-content-dark mb-[4px]">
                  {t('admin.dashboard.notes.title')}
                </h2>
                <p className="text-[13px] text-gray-900 dark:text-gray-700 mb-[14px] truncate">
                  {modal.user.email}
                </p>
                <textarea
                  autoFocus
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  maxLength={1000}
                  rows={5}
                  className="w-full rounded-[10px] border border-border-dark/[0.12] px-[14px] py-[10px] text-[14px] text-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-brand/30 mb-[16px]"
                  placeholder={t('admin.dashboard.notes.placeholder')}
                />
                <div className="flex items-center justify-between gap-[10px]">
                  {notesText.trim() ? (
                    <button
                      onClick={() => setNotesText('')}
                      className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
                    >
                      {t('admin.dashboard.notes.clear')}
                    </button>
                  ) : (
                    <span />
                  )}
                  <div className="flex gap-[10px]">
                    <button
                      onClick={() => setModal(null)}
                      className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={() =>
                        updateNotesMutation.mutate({
                          userId: modal.user.id,
                          notes: notesText
                        })
                      }
                      disabled={isPending}
                      className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 disabled:opacity-50 transition-colors"
                    >
                      {isPending
                        ? t('admin.dashboard.notes.pending')
                        : t('common.save')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {modal.type === 'download-stats' && (
              <>
                <div className="flex items-start justify-between mb-[16px]">
                  <h2 className="text-[18px] font-display font-bold text-content-dark">
                    {t('admin.dashboard.downloads.title')}
                  </h2>
                  <button
                    onClick={() => setModal(null)}
                    className="text-gray-600 hover:text-gray-900 text-[22px] leading-none mt-[2px] font-medium"
                  >
                    ×
                  </button>
                </div>
                {downloadStatsQuery.isLoading ? (
                  <div className="flex justify-center py-[32px]">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : (downloadStatsQuery.data?.byAsset ?? []).length === 0 ? (
                  <p className="text-[14px] text-gray-900 dark:text-gray-700 text-center py-[24px]">
                    {t('admin.dashboard.downloads.empty')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-[8px]">
                    {(downloadStatsQuery.data?.byAsset ?? []).map((asset) => (
                      <div
                        key={asset.slug}
                        className="flex items-center justify-between px-[12px] py-[10px] rounded-[10px] border border-border-dark/[0.08] bg-surface-card"
                      >
                        <div>
                          <p className="text-[14px] font-medium text-content-dark">
                            {asset.name}
                          </p>
                          <span className="text-[11px] text-gray-500 uppercase tracking-wider">
                            {asset.category}
                          </span>
                        </div>
                        <span className="text-[18px] font-bold text-content-dark">
                          {asset.downloads}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-[12px] py-[10px] mt-[4px] border-t border-border-dark/[0.1]">
                      <span className="text-[14px] font-semibold text-content-dark">
                        {t('admin.dashboard.downloads.total')}
                      </span>
                      <span className="text-[18px] font-bold text-content-dark">
                        {downloadStatsQuery.data?.totalDownloads ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {allUsers.length > 0 && (
        <div className="bg-gradient-to-r from-blue to-[#6d5fc0] rounded-[16px] px-[24px] py-[20px] mb-[16px] shadow-lg">
          <p
            style={{
              color: 'color-mix(in srgb, var(--color-bg-base) 70%, transparent)'
            }}
            className="text-[12px] font-semibold uppercase tracking-wider"
          >
            {t('admin.dashboard.mrr')}
          </p>
          <p className="text-[36px] font-bold text-surface-base mt-[4px]">
            $
            {(mrr / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
        </div>
      )}

      {allUsers.length > 0 && (
        <div className="grid grid-cols-2 tablet:grid-cols-6 gap-[12px] mb-[20px]">
          {[
            {
              label: t('admin.dashboard.stats.total'),
              value: allUsers.length,
              clickable: false
            },
            {
              label: t('admin.dashboard.stats.verified'),
              value: allUsers.filter((u) => u.isActivated).length,
              clickable: false
            },
            {
              label: t('admin.dashboard.stats.unactivated'),
              value: allUsers.filter((u) => !u.isActivated).length,
              clickable: false
            },
            {
              label: t('admin.dashboard.stats.paid'),
              value: allUsers.filter((u) =>
                u.License_key.some(
                  (k) =>
                    k.type === 'monthly' ||
                    k.type === 'annual' ||
                    k.type === 'yearly' ||
                    k.type === 'perpetual'
                )
              ).length,
              clickable: false
            },
            {
              label: t('admin.dashboard.stats.trial'),
              value: allUsers.filter((u) =>
                u.License_key.some((k) => k.type === 'trial')
              ).length,
              clickable: false
            },
            {
              label: t('admin.dashboard.stats.libraryDownloads'),
              value: totalDownloads,
              clickable: true
            }
          ].map((s) => (
            <div
              key={s.label}
              onClick={
                s.clickable ? () => setModal({ type: 'download-stats' }) : undefined
              }
              className={`bg-surface-base rounded-[12px] border border-border-dark/[0.18]] px-[16px] py-[12px] shadow-sm ${s.clickable ? 'cursor-pointer hover:border-blue hover:shadow-md transition-all' : ''}`}
            >
              <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                {s.label}
              </p>
              <p className="text-[22px] font-bold text-content-dark mt-[2px]">
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-[16px] gap-[12px] flex-wrap">
        <div className="relative w-full tablet:w-[300px]">
          <span className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[14px]">
            🔍
          </span>
          <input
            type="text"
            placeholder={t('admin.dashboard.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[38px] w-full pl-[36px] pr-[14px] rounded-[10px] border border-border-dark/[0.25] text-[14px] text-content-dark placeholder-gray-500 bg-surface-base shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>
        <button
          onClick={() => navigate(ROUTER_LINKS.ADMIN_CHANGELOG)}
          className="text-[13px] text-blue hover:underline transition-colors"
        >
          📋 {t('changelog.title')}
        </button>
      </div>

      <div className="flex gap-[6px] mb-[16px] flex-wrap">
        {[
          { id: 'all', label: t('admin.dashboard.filters.all') },
          { id: 'unactivated', label: t('admin.dashboard.filters.unactivated') },
          { id: 'paid', label: t('admin.dashboard.filters.paid') },
          { id: 'trial', label: t('admin.dashboard.filters.trial') },
          { id: 'expired', label: t('admin.dashboard.filters.expired') },
          { id: 'locked', label: t('admin.dashboard.filters.locked') }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-[12px] py-[5px] rounded-[8px] text-[12px] font-medium border transition-colors ${filter === f.id ? 'bg-blue text-surface-base border-blue' : 'bg-surface-base text-gray-900 dark:text-gray-700 border-border-dark/[0.25] hover:bg-surface-hover'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="w-full rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue to-[#6d5fc0] rounded-t-[18px]" />

        {usersQuery.isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden tablet:block overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-border-dark/[0.07] bg-surface-secondary-alt">
                    <SortHeader
                      label={t('admin.dashboard.columns.email')}
                      sortId="email"
                      className="text-left pl-[16px] pr-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.joined')}
                      sortId="createdAt"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.status')}
                      sortId="status"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.license')}
                      sortId="license"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.expires')}
                      sortId="expires"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.lastLogin')}
                      sortId="lastLogin"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <SortHeader
                      label={t('admin.dashboard.columns.trial')}
                      sortId="trial"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    {/* "Eye" is the Squarebit Eye product short name — verbatim. */}
                    <SortHeader
                      label="Eye"
                      sortId="eye"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    {/* "Riser" is the product name, verbatim, same as Eye. */}
                    <SortHeader
                      label="Riser"
                      sortId="riser"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    {/* "Framework" is the product short name, verbatim. */}
                    <SortHeader
                      label="Framework"
                      sortId="sbframework"
                      className="text-left px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider"
                    />
                    <th className="text-center px-[10px] py-[14px] text-[12px] font-semibold text-gray-900 dark:text-gray-700 uppercase tracking-wider">
                      {t('admin.dashboard.columns.notes')}
                    </th>
                    <th className="px-[10px] py-[14px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((user: IAdminUser, i: number) => {
                    const keys = user.License_key;
                    // Trial eligibility is account-wide, but if a trial key
                    // exists we can show which product it was claimed for.
                    const trialProduct = keys.find((k) => k.type === 'trial')
                      ?.product?.name;
                    return (
                      <tr
                        key={user.id}
                        className={`border-b border-border-dark/[0.05] ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface-card'}`}
                      >
                        <td className="pl-[16px] pr-[10px] py-[14px] font-medium text-content-dark max-w-[260px]">
                          <span className="flex items-center gap-[6px] flex-wrap">
                            <span className="truncate" title={user.email}>
                              {user.email}
                            </span>
                            {user.role === 'admin' && (
                              <span className="inline-flex items-center px-[6px] py-px rounded-[4px] bg-badge-purple-bg text-badge-purple-text text-[10px] font-semibold">
                                {user.role}
                              </span>
                            )}
                            {user.isLocked && (
                              <span
                                title={t('admin.dashboard.lockedTitle')}
                                className="inline-flex items-center gap-[4px] px-[6px] py-px rounded-[4px] bg-red-600 text-surface-base text-[10px] font-semibold cursor-default"
                              >
                                🔒 {t('admin.dashboard.locked')}
                              </span>
                            )}
                            {!user.isLocked && user.failedAttempts > 0 && (
                              <span
                                title={t('admin.dashboard.failedAttemptsTitle', {
                                  value: user.failedAttempts
                                })}
                                className="inline-flex items-center gap-[4px] px-[6px] py-px rounded-[4px] bg-amber-100 text-amber-700 text-[10px] font-semibold cursor-default"
                              >
                                ⚠ {user.failedAttempts}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-[10px] py-[14px] text-gray-900 dark:text-gray-700 whitespace-nowrap">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-[10px] py-[14px]">
                          {user.isActivated ? (
                            <span className="inline-flex items-center text-green-700 font-medium">
                              <span className="inline-flex items-center gap-[5px] px-[6px] py-px rounded-[4px] bg-badge-green-bg text-badge-green-text">
                                <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                                {t('admin.dashboard.verified')}
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-[6px]">
                              <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0" />
                              <button
                                onClick={() =>
                                  setModal({ type: 'account-actions', user })
                                }
                                title={t('admin.dashboard.accountActions.title')}
                                className="text-[16px] leading-none hover:opacity-70 transition-opacity"
                              >
                                ⚙️
                              </button>
                            </span>
                          )}
                        </td>
                        <td className="px-[10px] py-[14px]">
                          {keys.length > 0 ? (
                            <div className="flex flex-col gap-[4px]">
                              {keys.map((k) => (
                                <LicenseTypeBadge
                                  key={k.key}
                                  type={k.type}
                                  licenseKey={k.key}
                                  productName={k.product?.name}
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-900 dark:text-gray-700">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-[10px] py-[14px] text-gray-900 dark:text-gray-700 whitespace-nowrap">
                          {keys.length > 0 ? (
                            <div className="flex flex-col gap-[4px]">
                              {keys.map((k) => {
                                const isLicenseActive = new Date(k.expiresAt) >= now;
                                return (
                                  <span
                                    key={k.key}
                                    className={`text-[13px] inline-flex items-center gap-[4px] ${isLicenseActive ? '' : 'text-red-500'}`}
                                  >
                                    <span
                                      className={`w-[5px] h-[5px] rounded-full ${isLicenseActive ? 'bg-green-500' : 'bg-red-400'}`}
                                    />
                                    {formatDate(k.expiresAt)}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-900 dark:text-gray-700">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-[10px] py-[14px] whitespace-nowrap">
                          <button
                            onClick={() => setModal({ type: 'login-history', user })}
                            className="text-[13px] text-gray-900 dark:text-gray-700 hover:text-blue hover:underline transition-colors text-left"
                          >
                            {user.lastLoginAt ? (
                              formatDate(user.lastLoginAt)
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </button>
                        </td>
                        <td className="px-[10px] py-[14px]">
                          {user.trialAvailable ? (
                            <span className="inline-flex items-center text-green-700 font-medium">
                              <span className="inline-flex items-center gap-[5px] px-[6px] py-px rounded-[4px] bg-badge-green-bg text-badge-green-text">
                                <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                                {t('admin.dashboard.trialAvailable')}
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex flex-col items-start gap-[3px] font-medium">
                              <button
                                onClick={() =>
                                  setModal({ type: 'reset-trial', user })
                                }
                                title={
                                  trialProduct
                                    ? t('admin.dashboard.reallowTitleClaimed', {
                                        product: trialProduct
                                      })
                                    : t('admin.dashboard.reallowTitle')
                                }
                                className="inline-flex items-center justify-center px-[8px] py-[3px] gap-[5px] rounded-[6px] text-[11px] font-medium bg-badge-yellow-bg border border-badge-yellow-text text-badge-yellow-text hover:brightness-95 transition-colors"
                              >
                                <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0 animate-pulse" />
                                {t('admin.dashboard.reallow')}
                              </button>
                              {trialProduct && (
                                <span className="font-mono text-[9px] tracking-[0.06em] uppercase text-gray-500">
                                  {t('admin.dashboard.trialUsed')} ·{' '}
                                  {shortProductName(trialProduct)}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-[10px] py-[14px]">
                          <AccessPill
                            granted={user.eyeAccess}
                            grantedLabel={t('admin.dashboard.eye.granted')}
                            grantedTitle={t('admin.dashboard.eye.grantedTitle')}
                            noneTitle={t('admin.dashboard.eye.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: user.eyeAccess ? 'revoke-eye' : 'grant-eye',
                                user
                              })
                            }
                          />
                          {/* SEATS, not "Lic". The pill used to read the same
                              for everyone whether they held a licence or not,
                              so it said nothing - you had to open the modal to
                              find out. It now shows the seat total across this
                              account's non-revoked Eye licences, and a dash
                              when there are none, which is the fact an admin
                              is actually scanning this column for. Still opens
                              the same modal. */}
                          <button
                            onClick={() => setModal({ type: 'licenses', user })}
                            title={t('admin.dashboard.licenses.title')}
                            className={`ml-[4px] inline-flex items-center justify-center px-[7px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors hover:brightness-95 ${
                              user.eyeSeats == null
                                ? 'bg-surface-secondary-alt border-border-dark/[0.15] text-content-muted-light'
                                : 'bg-surface-secondary-alt border-border-dark/[0.15] text-content-muted'
                            }`}
                          >
                            {user.eyeSeats == null
                              ? '-'
                              : `${user.eyeSeats} ${t(
                                  'admin.dashboard.seats.short'
                                )}`}
                          </button>
                        </td>
                        <td className="px-[10px] py-[14px]">
                          <AccessPill
                            granted={hasGrant(user, RISER_PRODUCT_SLUG)}
                            grantedLabel={t('admin.dashboard.riser.granted')}
                            grantedTitle={t('admin.dashboard.riser.grantedTitle')}
                            noneTitle={t('admin.dashboard.riser.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: hasGrant(user, RISER_PRODUCT_SLUG)
                                  ? 'revoke-riser'
                                  : 'grant-riser',
                                user
                              })
                            }
                          />
                        </td>
                        <td className="px-[10px] py-[14px]">
                          <AccessPill
                            granted={hasGrant(user, SBFRAMEWORK_PRODUCT_SLUG)}
                            grantedLabel={t('admin.dashboard.framework.granted')}
                            grantedTitle={t(
                              'admin.dashboard.framework.grantedTitle'
                            )}
                            noneTitle={t('admin.dashboard.framework.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: hasGrant(user, SBFRAMEWORK_PRODUCT_SLUG)
                                  ? 'revoke-sbframework'
                                  : 'grant-sbframework',
                                user
                              })
                            }
                          />
                          {/* Granted but unkeyed: the account can download the
                              framework and not run it. Surfaced rather than
                              left to be noticed, with the retry attached. */}
                          {needsFrameworkKey(user) && (
                            <button
                              disabled={isPending}
                              onClick={() => issueKeyMutation.mutate(user.id)}
                              title={t('admin.dashboard.framework.noKeyTitle')}
                              className="mt-[4px] w-full inline-flex items-center justify-center px-[7px] py-[2px] rounded-[6px] text-[10.5px] font-medium bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 disabled:opacity-50 transition-colors"
                            >
                              {t('admin.dashboard.framework.issueKey')}
                            </button>
                          )}
                          {frameworkKey(user) && (
                            <button
                              onClick={() => setModal({ type: 'seats', user })}
                              title={t('admin.dashboard.seats.title')}
                              className="mt-[4px] w-full inline-flex items-center justify-center px-[7px] py-[2px] rounded-[6px] text-[10.5px] font-medium bg-surface-secondary-alt border border-border-dark/[0.15] text-content-muted hover:border-blue hover:text-blue transition-colors"
                            >
                              {t('admin.dashboard.seats.withCount', {
                                count: frameworkKey(user)?.maxActivations ?? 0
                              })}
                            </button>
                          )}
                        </td>
                        <td className="px-[10px] py-[14px] text-center">
                          <button
                            onClick={() => {
                              setNotesText(user.adminNotes ?? '');
                              setModal({ type: 'notes', user });
                            }}
                            title={user.adminNotes ?? t('admin.dashboard.addNote')}
                            className={`text-[18px] leading-none transition-opacity hover:opacity-70 ${user.adminNotes ? 'opacity-100' : 'opacity-30'}`}
                          >
                            📝
                          </button>
                        </td>
                        <td className="px-[10px] py-[14px] text-right">
                          {user.role !== 'admin' ? (
                            <button
                              onClick={() => setModal({ type: 'delete', user })}
                              className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-badge-red-bg border border-badge-red-text text-badge-red-text hover:brightness-95 transition-colors"
                            >
                              {t('admin.dashboard.delete')}
                            </button>
                          ) : (
                            <span
                              title={t('admin.dashboard.godMode')}
                              className="text-[18px]"
                            >
                              ⚡
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-[24px] py-[32px] text-center text-gray-900 dark:text-gray-700 text-[14px]"
                      >
                        {t('admin.dashboard.noMatches', { search })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card layout */}
            <div className="tablet:hidden flex flex-col">
              {pagedUsers.map((user: IAdminUser, i: number) => {
                const keys = user.License_key;
                const trialProduct = keys.find((k) => k.type === 'trial')?.product
                  ?.name;
                return (
                  <div
                    key={user.id}
                    className={`px-[16px] py-[14px] border-b border-border-dark/[0.05] ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface-card'}`}
                  >
                    <div className="flex items-start justify-between gap-[8px] mb-[10px]">
                      <div className="flex items-center gap-[6px] flex-wrap min-w-0">
                        <span className="font-medium text-[14px] text-content-dark break-all">
                          {user.email}
                        </span>
                        {user.role === 'admin' && (
                          <span className="inline-flex items-center px-[6px] py-px rounded-[4px] bg-badge-purple-bg text-badge-purple-text text-[10px] font-semibold shrink-0">
                            {user.role}
                          </span>
                        )}
                        {user.isLocked && (
                          <span className="inline-flex items-center gap-[4px] px-[6px] py-px rounded-[4px] bg-red-600 text-white text-[10px] font-semibold shrink-0">
                            🔒 Locked
                          </span>
                        )}
                        {!user.isLocked && user.failedAttempts > 0 && (
                          <span className="inline-flex items-center gap-[4px] px-[6px] py-px rounded-[4px] bg-amber-100 text-amber-700 text-[10px] font-semibold shrink-0">
                            ⚠ {user.failedAttempts}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-[6px] shrink-0">
                        <button
                          onClick={() => {
                            setNotesText(user.adminNotes ?? '');
                            setModal({ type: 'notes', user });
                          }}
                          title={user.adminNotes ?? t('admin.dashboard.addNote')}
                          className={`text-[16px] leading-none transition-opacity hover:opacity-70 ${user.adminNotes ? 'opacity-100' : 'opacity-30'}`}
                        >
                          📝
                        </button>
                        {user.role !== 'admin' ? (
                          <button
                            onClick={() => setModal({ type: 'delete', user })}
                            className="inline-flex items-center justify-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-badge-red-bg border border-badge-red-text text-badge-red-text hover:brightness-95 transition-colors"
                          >
                            {t('admin.dashboard.delete')}
                          </button>
                        ) : (
                          <span
                            title={t('admin.dashboard.godMode')}
                            className="text-[16px]"
                          >
                            ⚡
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-[12px] gap-y-[8px] text-[13px]">
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.joined')}
                        </span>
                        <div className="text-gray-900 dark:text-gray-700">
                          {formatDate(user.createdAt)}
                        </div>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.status')}
                        </span>
                        <div>
                          {user.isActivated ? (
                            <span className="inline-flex items-center text-green-700 font-medium">
                              <span className="inline-flex items-center gap-[5px] px-[6px] py-px rounded-[4px] bg-badge-green-bg text-badge-green-text">
                                <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                                {t('admin.dashboard.verified')}
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-[6px]">
                              <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0" />
                              <button
                                onClick={() =>
                                  setModal({ type: 'account-actions', user })
                                }
                                title={t('admin.dashboard.accountActions.title')}
                                className="text-[16px] leading-none hover:opacity-70 transition-opacity"
                              >
                                ⚙️
                              </button>
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.license')}
                        </span>
                        <div>
                          {keys.length > 0 ? (
                            <div className="flex flex-col gap-[4px]">
                              {keys.map((k) => (
                                <LicenseTypeBadge
                                  key={k.key}
                                  type={k.type}
                                  licenseKey={k.key}
                                  productName={k.product?.name}
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-900 dark:text-gray-700">
                              —
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.expires')}
                        </span>
                        <div className="text-gray-900 dark:text-gray-700">
                          {keys.length > 0 ? (
                            <div className="flex flex-col gap-[4px]">
                              {keys.map((k) => {
                                const isLicenseActive = new Date(k.expiresAt) >= now;
                                return (
                                  <span
                                    key={k.key}
                                    className={`inline-flex items-center gap-[4px] ${isLicenseActive ? '' : 'text-red-500'}`}
                                  >
                                    <span
                                      className={`w-[5px] h-[5px] rounded-full shrink-0 ${isLicenseActive ? 'bg-green-500' : 'bg-red-400 animate-pulse'}`}
                                    />
                                    {formatDate(k.expiresAt)}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.lastLogin')}
                        </span>
                        <div>
                          <button
                            onClick={() => setModal({ type: 'login-history', user })}
                            className="text-gray-900 dark:text-gray-700 hover:text-blue hover:underline transition-colors text-left"
                          >
                            {user.lastLoginAt ? (
                              formatDate(user.lastLoginAt)
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          {t('admin.dashboard.columns.trial')}
                        </span>
                        <div>
                          {user.trialAvailable ? (
                            <span className="inline-flex items-center text-green-700 font-medium">
                              <span className="inline-flex items-center gap-[5px] px-[6px] py-px rounded-[4px] bg-badge-green-bg text-badge-green-text">
                                <span className="w-[6px] h-[6px] rounded-full bg-green-500" />
                                {t('admin.dashboard.trialAvailable')}
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex flex-col items-start gap-[3px]">
                              <button
                                onClick={() =>
                                  setModal({ type: 'reset-trial', user })
                                }
                                title={
                                  trialProduct
                                    ? t('admin.dashboard.reallowTitleClaimed', {
                                        product: trialProduct
                                      })
                                    : t('admin.dashboard.reallowTitle')
                                }
                                className="inline-flex items-center justify-center px-[8px] py-[3px] gap-[5px] rounded-[6px] text-[11px] font-medium bg-badge-yellow-bg border border-badge-yellow-text text-badge-yellow-text hover:brightness-95 transition-colors"
                              >
                                <span className="w-[6px] h-[6px] rounded-full bg-red-500 shrink-0 animate-pulse" />
                                {t('admin.dashboard.reallow')}
                              </button>
                              {trialProduct && (
                                <span className="font-mono text-[9px] tracking-[0.06em] uppercase text-gray-500">
                                  {t('admin.dashboard.trialUsed')} ·{' '}
                                  {shortProductName(trialProduct)}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        {/* "Riser" is the product name, verbatim, same as Eye. */}
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          Riser
                        </span>
                        <div>
                          <AccessPill
                            granted={hasGrant(user, RISER_PRODUCT_SLUG)}
                            grantedLabel={t('admin.dashboard.riser.granted')}
                            grantedTitle={t('admin.dashboard.riser.grantedTitle')}
                            noneTitle={t('admin.dashboard.riser.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: hasGrant(user, RISER_PRODUCT_SLUG)
                                  ? 'revoke-riser'
                                  : 'grant-riser',
                                user
                              })
                            }
                          />
                        </div>
                      </div>
                      <div>
                        {/* "Framework" is the product short name, verbatim. */}
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          Framework
                        </span>
                        <div>
                          <AccessPill
                            granted={hasGrant(user, SBFRAMEWORK_PRODUCT_SLUG)}
                            grantedLabel={t('admin.dashboard.framework.granted')}
                            grantedTitle={t(
                              'admin.dashboard.framework.grantedTitle'
                            )}
                            noneTitle={t('admin.dashboard.framework.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: hasGrant(user, SBFRAMEWORK_PRODUCT_SLUG)
                                  ? 'revoke-sbframework'
                                  : 'grant-sbframework',
                                user
                              })
                            }
                          />
                        </div>
                      </div>
                      <div>
                        {/* "Eye" is the Squarebit Eye product short name — verbatim. */}
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                          Eye
                        </span>
                        <div>
                          <AccessPill
                            granted={user.eyeAccess}
                            grantedLabel={t('admin.dashboard.eye.granted')}
                            grantedTitle={t('admin.dashboard.eye.grantedTitle')}
                            noneTitle={t('admin.dashboard.eye.noneTitle')}
                            onClick={() =>
                              setModal({
                                type: user.eyeAccess ? 'revoke-eye' : 'grant-eye',
                                user
                              })
                            }
                          />
                          {/* SEATS, not "Lic". The pill used to read the same
                              for everyone whether they held a licence or not,
                              so it said nothing - you had to open the modal to
                              find out. It now shows the seat total across this
                              account's non-revoked Eye licences, and a dash
                              when there are none, which is the fact an admin
                              is actually scanning this column for. Still opens
                              the same modal. */}
                          <button
                            onClick={() => setModal({ type: 'licenses', user })}
                            title={t('admin.dashboard.licenses.title')}
                            className={`ml-[4px] inline-flex items-center justify-center px-[7px] py-[3px] rounded-[6px] text-[11px] font-medium border transition-colors hover:brightness-95 ${
                              user.eyeSeats == null
                                ? 'bg-surface-secondary-alt border-border-dark/[0.15] text-content-muted-light'
                                : 'bg-surface-secondary-alt border-border-dark/[0.15] text-content-muted'
                            }`}
                          >
                            {user.eyeSeats == null
                              ? '-'
                              : `${user.eyeSeats} ${t(
                                  'admin.dashboard.seats.short'
                                )}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredUsers.length === 0 && (
                <div className="px-[24px] py-[32px] text-center text-gray-900 dark:text-gray-700 text-[14px]">
                  {t('admin.dashboard.noMatches', { search })}
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex items-center justify-between px-[16px] py-[12px] border-t border-border-dark/[0.05] bg-surface-secondary text-[12px] text-gray-900 dark:text-gray-700">
          <span>
            {filteredUsers.length !== allUsers.length
              ? t('admin.dashboard.accountsFiltered', {
                  shown: filteredUsers.length,
                  total: allUsers.length
                })
              : t('admin.dashboard.accountsTotal', { total: allUsers.length })}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-[6px]">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-[8px] py-[3px] rounded-[6px] border border-gray-200 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-default transition-colors"
              >
                ‹
              </button>
              <span className="text-[12px] text-gray-900 dark:text-gray-700 min-w-[60px] text-center">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-[8px] py-[3px] rounded-[6px] border border-gray-200 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-default transition-colors"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
